import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../../components/SearchBar';
import TableComponent from '../../components/TableComponent';
import { showError, showToast } from '../../constants/toasts';
import MainContainer from '../../layout/MainContainer';
import {
  useLazyGetCustomerSupportQuery,
  useRevertQueryMutation,
} from '../../service/customerSupport';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { isValidInput } from '../../utils/validations';

const ManageCustomerSupport = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState<any[]>([]);
  const [getCustomerSupport] = useLazyGetCustomerSupportQuery();
  const [revertQuery] = useRevertQueryMutation();
  const [revertText, setRevertText] = useState('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [dialogValue, setDialogValue] = useState(false);

  const [openDetail, setOpenDetail] = useState(false);

  const [hidden, setHidden] = useState<string[]>([]);
  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);

  const open1 = Boolean(anchorEl1);

  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
  };
  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  const columns: GridColDef<(typeof data)[number]>[] = [
    {
      flex: 1,
      minWidth: 100,
      field: 'query',
      headerName: 'Query',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const queryText = row?.query || '';
        const truncatedText =
          queryText.length >= 50 ? queryText.slice(0, 50).concat('...') : queryText;

        return (
          <Tooltip title={queryText}>
            <div className="user_block">
              <p>{truncatedText}</p>
            </div>
          </Tooltip>
        );
      },
    },

    {
      flex: 1,
      minWidth: 100,
      field: 'revertQuery',
      headerName: 'RevertQuery',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.revertQuery || '-'}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 100,
      field: 'status',
      headerName: 'Resolved/Pending',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const status = row?.status;
        let statusText = '';

        if (status === 1) {
          statusText = 'Pending';
        } else if (status === 2) {
          statusText = 'Resolved';
        }

        return (
          <div className="user_block">
            <p>{statusText}</p>
          </div>
        );
      },
    },

    {
      flex: 0.1,
      minWidth: 80,
      field: 'action',
      headerName: 'Action',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions">
            <Box>
              <IconButton
                aria-controls={open1 ? 'basic-menu1' : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? 'true' : undefined}
                onClick={(e) => {
                  handleClick1(e);
                  setSelectedId(row?._id);
                }}
              >
                <img src="/static/images/more_icon.svg" alt="" />
              </IconButton>
              <Menu
                className="setting_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  'aria-labelledby': 'basic-button1',
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
                    borderRadius: '8px',
                    mt: 1,
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={() => {
                    setRevertText('');
                    // setSelectedId(row?._id);
                    setDialogValue(!dialogValue);
                  }}
                >
                  Revert Query
                </MenuItem>
              </Menu>
            </Box>
          </div>
        );
      },
    },
  ];

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const fetchCustomerSupport = async () => {
    setIsLoading(true);

    try {
      const response = await getCustomerSupport({
        page,
        limit: 10,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response.statusCode == 200) {
        setData(response?.data?.data);
        setTotalCount(response?.data?.count);
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevertQuery = async () => {
    if (revertText.length <= 0) {
      showError('Revert Query can`t be empty');
      return;
    }
    setDialogValue(false);
    const body = {
      ticketId: selectedId,
      revertQuery: revertText,
    };
    setAnchorEl1(null);
    setSelectedId('');
    setRevertText('');

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await revertQuery({
        body: encryptedBody,
      }).unwrap();
      if (response.statusCode === 200) {
        showToast(response?.message || 'Updated Successfully');
        setRevertText('');
        fetchCustomerSupport();
      } else {
        showError(response?.message);
      }
    } catch (e: any) {
      showError(e?.data?.message);
    }
  };

  useEffect(() => {
    fetchCustomerSupport();
  }, [page, debouncedSearchTerm]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1> Customer Support</h1>
        <p>
          <span onClick={() => navigate('/dashboard')}>Dashboard</span> - Customer Support
        </p>
      </div>

      <div className="cards">
        <Box className="custom_tabs" sx={{ width: '100%' }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm('')}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
          </div>

          <div className="sorting_table">
            <TableComponent
              columns={columns?.filter((item: any) => !hidden.includes(item.field))}
              loading={isLoading}
              rows={data}
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
            />
          </div>
        </Box>
      </div>

      <Dialog open={dialogValue} onClose={() => setDialogValue(false)}>
        <DialogTitle style={{ padding: '10px' }}>Revert Query</DialogTitle>
        <DialogContentText>
          <TextField
            hiddenLabel
            name="revert"
            className="text_field"
            style={{ margin: '10px', width: '320px' }}
            value={revertText}
            multiline
            onChange={(e) => {
              if (e.target.value == ' ') return;
              setRevertText(e.target.value);
            }}
          />
        </DialogContentText>
        <DialogActions>
          <Button
            className="btn btn_primary"
            style={{ padding: '0px', height: '40px' }}
            onClick={() => {
              setDialogValue(false);
              setRevertText('');
            }}
          >
            CANCEL
          </Button>
          <Button
            className="btn btn_primary"
            style={{ padding: '0px', height: '40px' }}
            onClick={() => {
              handleRevertQuery();
            }}
          >
            SEND
          </Button>
        </DialogActions>
      </Dialog>
      {/* <ConfirmationModal
        text={"Are you sure to delete this Promo Code ?"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDeletePromo}
      /> */}
    </MainContainer>
  );
};

export default ManageCustomerSupport;
